/* eslint-disable no-nested-ternary */
import {
  Fragment, ReactElement, useEffect, useMemo, useState,
} from 'react';
import { BackTop, Drawer } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import { BaseEntity, IdType } from '@rootCommon/types/common';
import { arrowPurpleUp } from '@rootCommon/images';
import {
  Loader, SwipeableList, SwipeableListItem, Empty,
} from '../index';
import DataListItem from './data-list-item';
import DataListItemSelectable from './data-list-item-selectable';
import DataListHeader from './data-list-header';
import DataListHeaderSelectable from './data-list-header-selectable';
import './DataList.scss';

type Props<T> = {
  items?: Array<T>;
  background?: (item: T) => ReactElement;
  header?: ReactElement;
  renderItem: (item: T) => ReactElement;
  multipleSelect?: (params: {
    checkedItems: IdType[];
    selectAllItems: (check: boolean) => void;
    maxCheckedItems?: number;
  }) => ReactElement;
  loadMore?: () => void;
  expandAll?: boolean;
  expandedArea?: (item: T) => ReactElement;
  maxCheckedItems?: number;
  isLoading?: boolean;
  showFooter?: boolean;
  onCheckItems?: (checkedItems: IdType[]) => void;
  backTopVisibilityHeight?: number;
};

// eslint-disable-next-line func-names
const DataList = function<T extends BaseEntity> ({
  items,
  background,
  renderItem,
  loadMore,
  header,
  multipleSelect,
  expandedArea,
  expandAll,
  maxCheckedItems,
  isLoading,
  showFooter,
  onCheckItems,
  backTopVisibilityHeight = 150,
}: Props<T>): JSX.Element {
  const [checkedItems, changeCheckedItems] = useState<IdType[]>([]);

  const allItemsIds: IdType[] | undefined = useMemo(() => items?.map((item) => item.id), [items]);
  const checkedItemsExist: boolean = useMemo(() => !!allItemsIds && checkedItems.every((item) => allItemsIds
    .includes(item)), [allItemsIds, checkedItems]);

  useEffect(() => {
    if (!!checkedItems.length && !checkedItemsExist) {
      changeCheckedItems([]);
    }
  }, [checkedItems.length, checkedItemsExist]);

  useEffect(() => {
    onCheckItems && onCheckItems(checkedItems);
  }, [checkedItems, onCheckItems]);

  const infiniteScroll = (list: ReactElement | ReactElement[]) => (loadMore ? (
    <InfiniteScroll initialLoad={false} pageStart={0} loadMore={loadMore} hasMore useWindow={false}>
      {list}
    </InfiniteScroll>
  ) : (
    list
  ));

  const handleMultipleSelect = (item: T) => (checked: boolean): void => {
    const addItem = (prevState: number[]) => {
      if (maxCheckedItems && prevState.length >= maxCheckedItems) {
        return prevState;
      }
      return prevState.concat(item.id);
    };
    const deleteItem = (prevState: number[]) => prevState.filter((v) => v !== item.id);

    changeCheckedItems((prevState) => (checked ? addItem(prevState) : deleteItem(prevState)));
  };

  const selectableItem = (item: T): ReactElement | ReactElement[] => (multipleSelect ? (
    <DataListItemSelectable checkItem={handleMultipleSelect(item)} value={checkedItems.includes(item.id)}>
      {renderItem(item)}
    </DataListItemSelectable>
  ) : (
    renderItem(item)
  ));

  const dataListItem = (item: T): JSX.Element => (expandedArea ? (
    <DataListItem>
      <div>{selectableItem(item)}</div>
      <div>{expandAll && expandedArea(item)}</div>
    </DataListItem>
  ) : (
    <DataListItem>{selectableItem(item)}</DataListItem>
  ));

  const swipeableList = (items: T[]): ReactElement | ReactElement[] => (background ? (
    <SwipeableList>
      {items.map((item) => (
        <SwipeableListItem key={item.id} swipeLeft={{ content: background(item) }}>
          {dataListItem(item)}
        </SwipeableListItem>
      ))}
    </SwipeableList>
  ) : (
    items.map((item) => <Fragment key={item.id}>{dataListItem(item)}</Fragment>)
  ));

  const selectAllItems = (check = true) => {
    changeCheckedItems(check && items ? items.map((v) => v.id) : []);
  };

  const dataListHeader = (Component: ReactElement) => (multipleSelect && !maxCheckedItems ? (
    <DataListHeaderSelectable
      selectAllItems={selectAllItems}
      selectedAll={checkedItems.length === items?.length}
    >
      {Component}
    </DataListHeaderSelectable>
  ) : (
    Component
  ));

  return (
    <div className="data-list-container">
      <div className="table">
        {header && (
          <div className="table__top">
            <DataListHeader>{dataListHeader(header)}</DataListHeader>
          </div>
        )}
        {isLoading ? (
          <div className="table__loader">
            <Loader />
          </div>
        ) : items?.length ? (
          <div className="table__body">
            {infiniteScroll(swipeableList(items))}
            <BackTop
              target={() => (document.getElementsByClassName('table__body')[0] as HTMLElement) || window}
              visibilityHeight={backTopVisibilityHeight}
            >
              <img src={arrowPurpleUp} alt="" />
            </BackTop>
          </div>
        ) : (
          <Empty />
        )}
        {multipleSelect && (
          <div>
            <Drawer
              mask={false}
              placement="bottom"
              closable={false}
              visible={(!!checkedItems.length || showFooter) && !isLoading}
              getContainer=".data-list-container"
              style={{ position: 'absolute' }}
              height="auto"
              className={showFooter ? 'show-footer' : ''}
            >
              {multipleSelect({ checkedItems, selectAllItems, maxCheckedItems })}
            </Drawer>
          </div>
        )}
      </div>

    </div>
  );
};

export default DataList;
